// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getClientPrompts, updateClientPrompts, resetClientPrompts, updateClientPersonality } from '../services/api';
import { Pencil, Trash2, Plus, RotateCcw, Bot } from 'lucide-react';
import Loader from '../components/common/Loader';
import { BOT_PERSONALITIES } from '../utils/bot_personalities';

interface Prompt {
    id: string;
    name: string;
    content: string;
    priority: number;
    category: string;
}

const categoryDisplayNames: Record<string, string> = {
    embeddings: 'knowledge',
    knowledge_fallback: 'knowledge fallback',
  };

const Prompts: React.FC = () => {
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isResetting, setIsResetting] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [customCategory, setCustomCategory] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedPersonality, setSelectedPersonality] = useState('');
    const [currentPersonality, setCurrentPersonality] = useState('');

    useEffect(() => {
        fetchPrompts();
        fetchCurrentPersonality();
    }, []);

    const fetchCurrentPersonality = async () => {
        try {
            const clientId = localStorage.getItem('client_id');
            if (!clientId) return;
            
            // For now, we'll get the personality from the prompts content
            // In a real implementation, you'd have an API endpoint to fetch client details
            // Since runtime injection is used, we can default to 'professional'
            const personality = 'professional'; // Default, will be overridden by runtime injection
            setCurrentPersonality(personality);
            setSelectedPersonality(personality);
        } catch (error) {
            console.error('Failed to fetch personality:', error);
            setCurrentPersonality('professional');
            setSelectedPersonality('professional');
        }
    };

    const fetchPrompts = async () => {
        try {
            const clientId = localStorage.getItem('client_id');
            if (!clientId) {
                toast.error('No client ID found. Please log in again.');
                setIsLoading(false);
                return;
            }
            const data = await getClientPrompts(clientId);
            const promptsList = data.prompts || [];
            setPrompts(promptsList);
            
            // Extract unique categories from prompts
            const uniqueCategories = [...new Set(promptsList.map(p => p.category))].filter(Boolean);
            setCategories(uniqueCategories);
            
            // Set default selected category if available
            if (uniqueCategories.length > 0) {
                setSelectedCategory(uniqueCategories[0]);
            }
        } catch (error) {
            toast.error('Failed to load prompts');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePrompt = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingPrompt) return;

        try {
            const clientId = localStorage.getItem('client_id');
            const category = customCategory ? customCategory.replace(/ /g, '_') : selectedCategory;
            const promptToSave = { ...editingPrompt, category };
            
            const updatedPrompts = editingPrompt.id.startsWith('new_') 
                ? [...prompts, { ...promptToSave, id: `prompt_${Date.now()}` }]
                : prompts.map(p => p.id === promptToSave.id ? promptToSave : p);
            
            const response = await updateClientPrompts(clientId, updatedPrompts);
            if (response.prompts) {
                const newPrompts = response.prompts;
                setPrompts(newPrompts);
                
                // Update categories
                const uniqueCategories = [...new Set(newPrompts.map(p => p.category))].filter(Boolean);
                setCategories(uniqueCategories);
                
                setEditingPrompt(null);
                setCustomCategory('');
                if (uniqueCategories.includes(category)) {
                    setSelectedCategory(category);
                } else if (uniqueCategories.length > 0) {
                    setSelectedCategory(uniqueCategories[0]);
                } else {
                    setSelectedCategory('');
                }
                toast.success('Prompt saved successfully');
            }
        } catch (error) {
            toast.error('Failed to save prompt');
        }
    };

    const handleAddNew = () => {
        // Default to first category if available
        const defaultCategory = categories.length > 0 ? categories[0] : 'general';
        setEditingPrompt({
            id: `new_${Date.now()}`,
            name: '',
            content: '',
            priority: 1,
            category: defaultCategory
        });
        setCustomCategory('');
        setSelectedCategory(defaultCategory);
    };

    const handleDeletePrompt = async (promptId: string) => {
        try {
            const updatedPrompts = prompts.filter(p => p.id !== promptId);
            const clientId = localStorage.getItem('client_id');
            const response = await updateClientPrompts(clientId, updatedPrompts);
            if (response.prompts) {
                const newPrompts = response.prompts;
                setPrompts(newPrompts);
                
                // Update categories
                const uniqueCategories = [...new Set(newPrompts.map(p => p.category))].filter(Boolean);
                setCategories(uniqueCategories);
                
                toast.success('Prompt deleted successfully');
            }
            setShowDeleteConfirm(null);
        } catch (error) {
            toast.error('Failed to delete prompt');
        }
    };

    const handleResetPrompts = async () => {
        try {
            setIsResetting(true);
            const clientId = localStorage.getItem('client_id');
            
            // If personality changed, update it first
            if (selectedPersonality && selectedPersonality !== currentPersonality) {
                try {
                    await updateClientPersonality(clientId, selectedPersonality);
                    setCurrentPersonality(selectedPersonality);
                    toast.success(`Personality updated to ${BOT_PERSONALITIES[selectedPersonality].name}`);
                } catch (error) {
                    toast.error('Failed to update personality');
                    // Continue with reset even if personality update fails
                }
            }
            
            const response = await resetClientPrompts(clientId);
            
            if (response.prompts) {
                const newPrompts = response.prompts;
                setPrompts(newPrompts);
                
                // Update categories
                const uniqueCategories = [...new Set(newPrompts.map(p => p.category))].filter(Boolean);
                setCategories(uniqueCategories);
                
                // Reset selected category
                if (uniqueCategories.length > 0) {
                    setSelectedCategory(uniqueCategories[0]);
                } else {
                    setSelectedCategory('');
                }
                
                toast.success('Prompts have been reset to default values');
            }
            setShowResetConfirm(false);
        } catch (error) {
            toast.error('Failed to reset prompts to default values');
        } finally {
            setIsResetting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="h-full min-h-[400px] relative">
                <Loader 
                    fullPage={false} 
                    text="Loading prompts..." 
                    variant="pulse"
                    overlay={true} 
                />
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold">Prompts</h1>
                
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowResetConfirm(true)}
                        className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
                    >
                        <RotateCcw size={14} />
                        Reset to Default
                    </button>
                    <button
                        onClick={handleAddNew}
                        className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2"
                    >
                        <Plus size={16} />
                        Add New Prompt
                    </button>
                </div>
            </div>

            <div className="grid gap-6">
                {prompts.map(prompt => (
                    <div key={prompt.id} className="bg-white rounded-card p-6 shadow-card transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">{prompt.name}</h3>
                                <span className="inline-block px-3 py-1 rounded-full text-sm bg-brand-100 text-brand-700">
                                    {categoryDisplayNames[prompt.category] ?? prompt.category.replace(/_/g, ' ')}
                                </span>
                                <span className="ml-2 text-sm text-gray-500">
                                    Priority: {prompt.priority}
                                </span>
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => {
                                        setEditingPrompt(prompt);
                                        setSelectedCategory(prompt.category);
                                        setCustomCategory('');
                                    }}
                                    className="text-brand-500 hover:text-brand-700 p-2 rounded-full hover:bg-brand-50"
                                >
                                    <Pencil size={14} />
                                </button>
                                <button
                                    onClick={() => setShowDeleteConfirm(prompt.id)}
                                    className="text-red-600 hover:text-red-800 p-2 rounded-full hover:bg-red-50"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{prompt.content}</p>
                    </div>
                ))}
            </div>

            {/* Edit/Add Prompt Modal */}
            {editingPrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-card p-6 max-w-2xl w-full">
                        <h2 className="text-xl font-semibold mb-4">
                            {editingPrompt.id.startsWith('new_') ? 'Add New Prompt' : 'Edit Prompt'}
                        </h2>
                        <form onSubmit={handleSavePrompt}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Name</label>
                                    <input
                                        type="text"
                                        value={editingPrompt.name}
                                        onChange={(e) => setEditingPrompt({
                                            ...editingPrompt,
                                            name: e.target.value
                                        })}
                                        className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Category</label>
                                    <div className="flex gap-2">
                                        <select
                                            value={selectedCategory}
                                            onChange={(e) => {
                                                setSelectedCategory(e.target.value);
                                                setCustomCategory('');
                                            }}
                                            className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{categoryDisplayNames[cat] ?? cat.replace(/_/g, ' ')}</option>
                                            ))}
                                            <option value="custom">Custom Category</option>
                                        </select>
                                        {selectedCategory === 'custom' && (
                                            <input
                                                type="text"
                                                value={customCategory}
                                                onChange={(e) => setCustomCategory(e.target.value)}
                                                placeholder="Enter custom category"
                                                className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2"
                                            />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Priority</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={editingPrompt.priority}
                                        onChange={(e) => setEditingPrompt({
                                            ...editingPrompt,
                                            priority: parseInt(e.target.value)
                                        })}
                                        className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Content</label>
                                    <textarea
                                        value={editingPrompt.content}
                                        onChange={(e) => setEditingPrompt({
                                            ...editingPrompt,
                                            content: e.target.value
                                        })}
                                        rows={6}
                                        className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2"
                                        required
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        <Bot size={16} className="mr-1 inline-block" />
                                        Note: The bot's personality traits are automatically applied to all prompts at runtime.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setEditingPrompt(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-card p-6 max-w-md w-full">
                        <h2 className="text-xl font-semibold mb-4">Confirm Delete</h2>
                        <p className="text-gray-600 mb-6">Are you sure you want to delete this prompt? This action cannot be undone.</p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeletePrompt(showDeleteConfirm)}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Confirmation Modal */}
            {showResetConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-card p-6 max-w-md w-full">
                        <h2 className="text-xl font-semibold mb-4">Reset to Default Prompts</h2>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to reset all prompts to the default values? This will replace all your custom prompts with the system defaults.
                        </p>
                        
                        {/* Personality Selection */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Bot size={16} className="mr-2 inline-block" />
                                Bot Personality (Optional)
                            </label>
                            <p className="text-xs text-gray-500 mb-3">
                                You can also change the bot's personality when resetting prompts.
                            </p>
                            <select
                                value={selectedPersonality}
                                onChange={(e) => setSelectedPersonality(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            >
                                {Object.entries(BOT_PERSONALITIES).map(([key, personality]) => (
                                    <option key={key} value={key}>
                                        {personality.name} - {personality.description}
                                    </option>
                                ))}
                            </select>
                            {selectedPersonality !== currentPersonality && (
                                <p className="text-xs text-brand-500 mt-2">
                                    <Bot size={16} className="mr-1 inline-block" />
                                    Personality will change from {BOT_PERSONALITIES[currentPersonality]?.name || 'Professional'} to {BOT_PERSONALITIES[selectedPersonality].name}
                                </p>
                            )}
                        </div>
                        
                        <div className="border-t pt-4">
                            <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowResetConfirm(false);
                                        setSelectedPersonality(currentPersonality);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleResetPrompts}
                                    disabled={isResetting}
                                    className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 flex items-center gap-2"
                                >
                                    {isResetting ? (
                                        <>
                                            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                                            Resetting...
                                        </>
                                    ) : (
                                        <>
                                            <RotateCcw size={14} />
                                            Reset to Default
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Prompts; 